import { createSelector } from 'reselect';
import { BUYER, CO_BUYER, TRADE_IN, VEHICLE_ENTITY } from '@makemydeal/dr-shared-types';
import type { StateTree } from '@makemydeal/dr-dash-types';

import type {
    AioFsSyncFailure,
    AioFsSyncFailureRetryState,
    FsBannerSeverity,
    FsEntityKey,
    FsFailureGroup,
    FsSyncFailureViewModel,
    NormalizedError,
    NormalizedErrorCategory,
    NormalizedErrorHints
} from '../types/fsSyncFailure';

const UNNAMED_FIELD = 'unknown';
const FINANCE_SUMMARY_ROOT = 'financesummary';
const TRADE_SCOPED_FINANCE_SUMMARY_FIELD = 'nettrade';
const TAX_SCOPED_FINANCE_SUMMARY_FIELD = 'salestax';
const RATE_SCOPED_FINANCE_SUMMARY_FIELD = 'requestedapr';
const PROTECTION_ENTITY: FsEntityKey = 'protection';
const TAXES_ENTITY: FsEntityKey = 'taxes';
const RATE_ENTITY: FsEntityKey = 'rate';
const FEES_ENTITY: FsEntityKey = 'fees';
const UNROUTED_ENTITY: FsEntityKey = 'other';

// FS's field-name casing on sourcePath isn't guaranteed consistent, so every comparison here is lowercase.
const PROTECTION_FINANCE_SUMMARY_FIELDS = ['gap', 'warranty', 'creditlifeins', 'accidenthealthins'];
const FEES_FINANCE_SUMMARY_FIELDS = ['acquisitionfees', 'governmentfees', 'frontendfees', 'otherfees'];
const VEHICLE_SCOPED_FIELDS = ['stocknumber', 'vin'];

const ENTITY_BY_PATH_ROOT: Record<string, FsEntityKey> = {
    applicant: BUYER,
    coApplicant: CO_BUYER,
    vehicle: VEHICLE_ENTITY,
    tradeIns: TRADE_IN
};

const ENTITY_BY_PARTICIPANT: Record<string, FsEntityKey> = {
    applicant: BUYER,
    coApplicant: CO_BUYER
};

const FS_ENTITY_ORDER: FsEntityKey[] = [
    UNROUTED_ENTITY,
    BUYER,
    CO_BUYER,
    FEES_ENTITY,
    PROTECTION_ENTITY,
    RATE_ENTITY,
    TAXES_ENTITY,
    TRADE_IN,
    VEHICLE_ENTITY
];

const DISPLAY_CATEGORY_RANK: Record<NormalizedErrorCategory, number> = {
    aioInternalError: 8,
    dealerConfig: 7,
    missingBuyer: 6,
    incompleteBusinessSigner: 5,
    configuration: 4,
    transient: 3,
    fieldData: 2,
    participantRollup: 2,
    unknown: 1
};

const SUPPORT_ROUTED_CATEGORIES: ReadonlySet<NormalizedErrorCategory> = new Set([
    'aioInternalError',
    'configuration',
    'dealerConfig',
    'missingBuyer',
    'incompleteBusinessSigner'
]);

const getFsSyncFailure = (state: StateTree): AioFsSyncFailure | undefined => state.baseDeal?.aioDxgEvents?.fsSyncFailure;

export const getFsNormalizedErrors = (state: StateTree): NormalizedError[] => getFsSyncFailure(state)?.normalizedErrors ?? [];

export const getFsCoxAutoCorrelationId = (state: StateTree): string | undefined => getFsSyncFailure(state)?.coxAutoCorrelationId;

export const getFsRetryState = (state: StateTree): AioFsSyncFailureRetryState => {
    const fsSyncFailure = getFsSyncFailure(state);

    return {
        retryable: fsSyncFailure?.retryable,
        statusCode: fsSyncFailure?.statusCode,
        attempt: fsSyncFailure?.attempt,
        maxAttempts: fsSyncFailure?.maxAttempts
    };
};

const resolveFinanceSummaryEntity = (field: string): FsEntityKey => {
    const normalizedField = field.toLowerCase();

    if (normalizedField === TRADE_SCOPED_FINANCE_SUMMARY_FIELD) {
        return TRADE_IN;
    }

    if (normalizedField === TAX_SCOPED_FINANCE_SUMMARY_FIELD) {
        return TAXES_ENTITY;
    }

    if (normalizedField === RATE_SCOPED_FINANCE_SUMMARY_FIELD) {
        return RATE_ENTITY;
    }

    if (PROTECTION_FINANCE_SUMMARY_FIELDS.includes(normalizedField)) {
        return PROTECTION_ENTITY;
    }

    return FEES_FINANCE_SUMMARY_FIELDS.includes(normalizedField) ? FEES_ENTITY : UNROUTED_ENTITY;
};

export const resolveFsEntityFromError = (error: NormalizedError): FsEntityKey | undefined => {
    const segments = error.sourcePath?.split('.') ?? [];

    if (segments.length > 1) {
        const [root, field] = segments;

        return root.toLowerCase() === FINANCE_SUMMARY_ROOT
            ? resolveFinanceSummaryEntity(field)
            : ENTITY_BY_PATH_ROOT[root] ?? UNROUTED_ENTITY;
    }

    if (VEHICLE_SCOPED_FIELDS.includes(error.canonicalField)) {
        return VEHICLE_ENTITY;
    }

    if (error.participant) {
        return ENTITY_BY_PARTICIPANT[error.participant];
    }

    return error.canonicalField === UNNAMED_FIELD ? undefined : UNROUTED_ENTITY;
};

const toHintsKey = (hints?: NormalizedErrorHints): string =>
    hints
        ? JSON.stringify(
              Object.keys(hints)
                  .sort()
                  .map((member) => [member, hints[member as keyof NormalizedErrorHints]])
          )
        : '';

const resolveDisplayCategory = (normalizedErrors: NormalizedError[]): NormalizedErrorCategory | undefined =>
    normalizedErrors.reduce<NormalizedErrorCategory | undefined>(
        (winner, error) =>
            winner === undefined || DISPLAY_CATEGORY_RANK[error.category] > DISPLAY_CATEGORY_RANK[winner] ? error.category : winner,
        undefined
    );

const resolveSeverity = (category: NormalizedErrorCategory, retryState: AioFsSyncFailureRetryState): FsBannerSeverity => {
    if (category !== 'transient') {
        return 'error';
    }

    const { attempt, maxAttempts } = retryState;

    return attempt !== undefined && maxAttempts !== undefined && attempt < maxAttempts ? 'info' : 'caution';
};

const buildLines = (normalizedErrors: NormalizedError[]) => {
    const groupsByEntity = new Map<FsEntityKey, FsFailureGroup>();
    const seenFieldKeys = new Set<string>();
    let hasGenericLine = false;

    const groupFor = (entity: FsEntityKey): FsFailureGroup => {
        const existing = groupsByEntity.get(entity);

        if (existing) {
            return existing;
        }

        const created: FsFailureGroup = { entity, fields: [] };
        groupsByEntity.set(entity, created);

        return created;
    };

    normalizedErrors.forEach((error) => {
        const entity = resolveFsEntityFromError(error);

        if (error.canonicalField === UNNAMED_FIELD) {
            if (entity && entity !== UNROUTED_ENTITY) {
                groupFor(entity);
            } else {
                hasGenericLine = true;
            }

            return;
        }

        const fieldKey = `${entity}|${error.participant}|${error.canonicalField}|${toHintsKey(error.hints)}`;

        if (seenFieldKeys.has(fieldKey)) {
            return;
        }

        seenFieldKeys.add(fieldKey);
        groupFor(entity as FsEntityKey).fields.push({
            canonicalField: error.canonicalField,
            participant: error.participant,
            ...(error.hints && { hints: error.hints }),
            sensitive: error.sensitive
        });
    });

    const groups = FS_ENTITY_ORDER.map((entity) => groupsByEntity.get(entity)).filter((group): group is FsFailureGroup => !!group);

    return { groups, hasGenericLine };
};

const buildViewModel = (fsSyncFailure?: AioFsSyncFailure): FsSyncFailureViewModel => {
    const normalizedErrors = fsSyncFailure?.normalizedErrors ?? [];
    const category = resolveDisplayCategory(normalizedErrors);
    const errorCount = normalizedErrors.length;

    if (!fsSyncFailure || category === undefined) {
        return { groups: [], hasGenericLine: false, errorCount };
    }

    const severity = resolveSeverity(category, fsSyncFailure);

    if (SUPPORT_ROUTED_CATEGORIES.has(category)) {
        return { category, severity, groups: [], hasGenericLine: false, errorCount };
    }

    const { groups, hasGenericLine } = buildLines(normalizedErrors);

    return { category, severity, groups, hasGenericLine, errorCount };
};

export const getFsSyncFailureViewModel = createSelector([getFsSyncFailure], buildViewModel);
